import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { ActionMenu, ActionMenuItem } from 'src/components/action-menu'
import { cn } from 'src/utils/style'
import { ListItemRow } from 'src/components/layout/item-collection'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { TemplateBodyPreview } from './template-body-preview'
import {
  templateStatusEnum,
  TemplateStatusValue,
  WhatsappTemplate,
} from 'src/services/whatsapp/models'

// Status drives the card outline and its header strip so an admin can scan
// approval state across the grid.
const STATUS_STYLE: Record<TemplateStatusValue, { border: string; header: string }> = {
  [templateStatusEnum.DRAFT]: { border: 'border-accent-200', header: 'bg-accent-50 text-accent' },
  [templateStatusEnum.APPROVED]: { border: 'border-green-300', header: 'bg-green-50 text-green' },
  [templateStatusEnum.PENDING]: {
    border: 'border-yellow-300',
    header: 'bg-yellow-50 text-yellow-800',
  },
  [templateStatusEnum.REJECTED]: {
    border: 'border-error-300',
    header: 'bg-error-50 text-error-700',
  },
  [templateStatusEnum.DISABLED]: {
    border: 'border-gray-300',
    header: 'bg-gray-100 text-text-secondary',
  },
}

const STATUS_LABEL_KEY: Record<TemplateStatusValue, string> = {
  [templateStatusEnum.DRAFT]: 'whatsappTemplates.statusDraft',
  [templateStatusEnum.APPROVED]: 'whatsappTemplates.statusApproved',
  [templateStatusEnum.PENDING]: 'whatsappTemplates.statusPending',
  [templateStatusEnum.REJECTED]: 'whatsappTemplates.statusRejected',
  [templateStatusEnum.DISABLED]: 'whatsappTemplates.statusDisabled',
}

interface TemplateCardProps {
  template: WhatsappTemplate
  onDisable: (id: string) => void
  isDisabling: boolean
  onEnable: (id: string) => void
  isEnabling: boolean
  onDelete: (id: string) => void
  isDeleting: boolean
  onEdit: (template: WhatsappTemplate) => void
  onCheckStatus: (id: string) => void
  isCheckingStatus: boolean
  onDuplicate: (template: WhatsappTemplate) => void
  onSubmit: (id: string) => void
  isSubmitting: boolean
  viewMode?: ViewMode
}

export const TemplateCard: FC<TemplateCardProps> = ({
  template: tpl,
  onDisable,
  isDisabling,
  onEnable,
  isEnabling,
  onDelete,
  isDeleting,
  onEdit,
  onCheckStatus,
  isCheckingStatus,
  onDuplicate,
  onSubmit,
  isSubmitting,
  viewMode = 'card',
}) => {
  const { t } = useTranslation()
  const isDraft = tpl.status === templateStatusEnum.DRAFT
  // Drafts and rejected rows are the only editable ones.
  const isEditable = isDraft || tpl.status === templateStatusEnum.REJECTED

  const actions: ActionMenuItem[] = [
    ...(isEditable
      ? [
          {
            key: 'edit',
            label: t('whatsappTemplates.edit'),
            icon: <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />,
            onClick: () => onEdit(tpl),
            testId: `edit-template-${tpl.id}`,
          },
        ]
      : []),
    ...(isDraft
      ? [
          {
            key: 'submit',
            label: t('whatsappTemplates.submit'),
            icon: <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />,
            onClick: () => onSubmit(tpl.id),
            disabled: isSubmitting,
            testId: `submit-template-${tpl.id}`,
          },
        ]
      : []),
    ...(tpl.status === templateStatusEnum.PENDING
      ? [
          {
            key: 'check-status',
            label: t('whatsappTemplates.checkStatus'),
            icon: <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />,
            onClick: () => onCheckStatus(tpl.id),
            disabled: isCheckingStatus,
            testId: `check-status-${tpl.id}`,
          },
        ]
      : []),
    {
      key: 'duplicate',
      label: t('whatsappTemplates.duplicate'),
      icon: <DocumentDuplicateIcon className="h-4 w-4" aria-hidden="true" />,
      onClick: () => onDuplicate(tpl),
      testId: `duplicate-template-${tpl.id}`,
    },
    ...(tpl.status === templateStatusEnum.DISABLED
      ? [
          {
            key: 'enable',
            label: t('whatsappTemplates.enable'),
            icon: <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />,
            onClick: () => onEnable(tpl.id),
            disabled: isEnabling,
            testId: `enable-template-${tpl.id}`,
          },
        ]
      : isDraft
      ? []
      : [
          {
            key: 'disable',
            label: t('whatsappTemplates.disable'),
            icon: <NoSymbolIcon className="h-4 w-4" aria-hidden="true" />,
            onClick: () => onDisable(tpl.id),
            disabled: isDisabling,
            testId: `disable-template-${tpl.id}`,
          },
        ]),
    {
      key: 'delete',
      label: t('whatsappTemplates.delete'),
      icon: <TrashIcon className="h-4 w-4" aria-hidden="true" />,
      onClick: () => onDelete(tpl.id),
      disabled: isDeleting,
      tone: 'error',
      testId: `delete-template-${tpl.id}`,
    },
  ]

  const actionMenu = (
    <ActionMenu
      items={actions}
      label={t('whatsappTemplates.actions')}
      testId={`template-actions-${tpl.id}`}
    />
  )

  const isSubmitted =
    tpl.status === templateStatusEnum.PENDING || tpl.status === templateStatusEnum.APPROVED
  const rejectionText =
    tpl.status === templateStatusEnum.REJECTED && tpl.rejectionReason
      ? t('whatsappTemplates.rejectionReason', { reason: tpl.rejectionReason })
      : null

  if (viewMode === 'list') {
    return (
      <ListItemRow
        className={cn(['border-2', STATUS_STYLE[tpl.status].border])}
        data-testid={`template-row-${tpl.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="truncate font-semibold text-text-primary" title={tpl.name}>
              {tpl.name}
            </h3>
            {tpl.isDefault ? (
              <span className="shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary">
                {t('whatsappTemplates.defaultBadge')}
              </span>
            ) : null}
          </div>
          <p className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-placeholder">
            <span className="whitespace-nowrap uppercase">{tpl.language}</span>
            <span className="whitespace-nowrap">{tpl.category}</span>
            {isSubmitted ? (
              <span
                className="min-w-0 truncate text-xs text-text-secondary"
                title={t('whatsappTemplates.noEditSubmitted')}
                data-testid={`template-no-edit-${tpl.id}`}
              >
                {t('whatsappTemplates.noEditSubmitted')}
              </span>
            ) : null}
            {rejectionText ? (
              <span className="min-w-0 truncate text-xs text-error-700" title={rejectionText}>
                {rejectionText}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn([
              'rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
              STATUS_STYLE[tpl.status].header,
            ])}
            data-testid={`template-status-${tpl.id}`}
          >
            {t(STATUS_LABEL_KEY[tpl.status])}
          </span>
          {actionMenu}
        </div>
      </ListItemRow>
    )
  }

  return (
    <div
      className={cn([
        'flex h-full flex-col rounded-xl border-2 bg-surface shadow-sm transition-shadow',
        STATUS_STYLE[tpl.status].border,
      ])}
      data-testid={`template-row-${tpl.id}`}
    >
      <div
        className={cn([
          'flex items-center justify-between gap-2 rounded-t-[10px] py-1 pl-4 pr-2',
          STATUS_STYLE[tpl.status].header,
        ])}
      >
        <span
          className="text-xs font-semibold uppercase tracking-wide"
          data-testid={`template-status-${tpl.id}`}
        >
          {t(STATUS_LABEL_KEY[tpl.status])}
        </span>
        {actionMenu}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-text-primary">{tpl.name}</span>
          {tpl.isDefault ? (
            <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary">
              {t('whatsappTemplates.defaultBadge')}
            </span>
          ) : null}
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-text-secondary">
            {tpl.category}
          </span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs uppercase text-text-secondary">
            {tpl.language}
          </span>
        </div>

        <TemplateBodyPreview body={tpl.body} className="mt-3" />

        {isSubmitted ? (
          <p
            className="mt-2 text-xs text-text-secondary"
            data-testid={`template-no-edit-${tpl.id}`}
          >
            {t('whatsappTemplates.noEditSubmitted')}
          </p>
        ) : null}

        {rejectionText ? <p className="mt-2 text-xs text-error-700">{rejectionText}</p> : null}
      </div>
    </div>
  )
}
